import React, { useEffect, useRef } from "react";
import {
  Database,
  ClipboardCheck,
  Activity,
  School,
  UserPen,
  CheckCircle,
  Zap,
  Globe,
  Sparkles,
  Layers,
  Award
} from "lucide-react";
import SEO from "../components/SEO";
import "./ICampusBEAT.css";

const ICampusBEAT = () => {
  const observerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-beat-in-view');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.beat-reveal-scroll');
    hiddenElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;

    // Pulse shimmer
    const shimmer = card.querySelector('.beat-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(0,132,255,0.2) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const shimmer = card.querySelector('.beat-card-shimmer');
    if (shimmer) {
      shimmer.style.background = `transparent`;
    }
  };

  const modules = [
    {
      title: "Sources & Masters",
      icon: <Database size={32} />,
      img: "./images/campusimages/Masters.png",
      badge: "Foundation",
      points: [
        "Programs, Branches & Course Grades",
        "Exam Notifications & Fee Masters",
        "CGPA / SGPA Range Mapping",
        "Student & Employee Data Masters",
        "Auto Curriculum updates for detainees"
      ]
    },
    {
      title: "Exam Registration",
      icon: <ClipboardCheck size={32} />,
      img: "./images/campusimages/Exam-Register.png",
      badge: "Operations",
      points: [
        "RV/RC/RVC Registration Workflows",
        "Room Allotment & Priority Logic",
        "OMR Security Code Generation",
        "Barcoded Photo Nominal Rolls",
        "Single & Bulk Hall Ticket Downloads"
      ]
    },
    {
      title: "OMR Processing",
      icon: <Layers size={32} />,
      img: "./images/campusimages/omr-processin.png",
      badge: "Scanning",
      points: [
        "Internal & Practical Marks Entry",
        "Real-time Absentees Management",
        "OMR Recognition & On-screen Verification",
        "Scan-first, Verify-later technology",
        "Mismatched sheet auto-detection"
      ]
    },
    {
      title: "Result Engine",
      icon: <Award size={32} />,
      img: "./images/campusimages/result-processing.png",
      badge: "Publication",
      points: [
        "NBA-aligned SGPA/CGPA calculations",
        "Moderation, Gracing & Grafting rules",
        "Topper & Backlog list generation",
        "Results posted to Student Portals",
        "Course & Semester-wise analytics"
      ]
    },
    {
      title: "Outcome (OBE)",
      icon: <Activity size={32} />,
      img: "./images/campusimages/obe-beat.png",
      badge: "Accreditation",
      points: [
        "Define COs, POs with threshold mapping",
        "CO-PO correlation & relationship mapping",
        "Direct & Indirect Attainment tracking",
        "NBA-compliant reporting & insights",
        "Semester-based academic planning"
      ]
    },
    {
      title: "Digital Eval (Pro)",
      icon: <UserPen size={32} />,
      img: "./images/campusimages/digital-evaluation.png",
      badge: "Evaluation",
      points: [
        "Schema-based question paper structure",
        "Barcode-masked student identity",
        "V1, V2 & Revaluation workflows",
        "Remote evaluator role assignments",
        "One-click data push to main server"
      ]
    }
  ];

  return (
    <div className="premium-beat-wrapper">
      <SEO
        title="iCampus BEAT — Examination Automation & Evaluation Suite | DBS"
        description="Digital examination and OMR processing software supporting fast valuation, instant marks compilation, NBA-aligned OBE attainment, and secure student portal result publication."
        keywords="iCampus BEAT, digital evaluation software, OMR examination system, OBE attainment software, college examination software India"
        canonical="https://dbasesolutions.in/icampusbeat"
        ogImage="/og-icampusbeat.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Products", url: "/products" },
          { name: "iCampus BEAT", url: "/icampusbeat" }
        ]}
      />
      {/* Dynamic Pulse Background */}
      <div className="beat-pulse-bg">
        <div className="pulse-orb p-o1"></div>
        <div className="pulse-orb p-o2"></div>
        <div className="pulse-waves">
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`wave-beat b${i}`}></div>
          ))}
        </div>
      </div>

      <div className="beat-content-inner">
        <header className="beat-hero-section beat-reveal-scroll">
          <div className="beat-badge-top">
            <Activity size={16} className="beat-icon-pulse" />
            <span>Smart Exam Automation Suite</span>
          </div>
          <div className="beat-logo-container">
            <img src="./images/icampus_Beat.png" alt="iCampus BEAT" className="beat-logo-3d" />
          </div>
          <h1 className="beat-hero-title">
            iCampus <span className="text-glow-beat">BEAT</span>
          </h1>
          <p className="beat-hero-desc">
            End-to-End Automation for Examination, Evaluation, and Result Processing. Reducing manual efforts with revolutionary technological patterns.
          </p>
          <div className="beat-hero-icons">
            <Sparkles className="float-b i1" />
            <Globe className="float-b i2" />
            <Zap className="float-b i3" />
          </div>
        </header>

        <section className="beat-overview-glass beat-reveal-scroll">
          <div className="overview-card-beat">
            <div className="overview-top">
              <School size={32} />
              <h2>Global Overview</h2>
            </div>
            <p>
             DBS iCampus BEAT is One-stop-destination for examination, preparation, results, reports and more. Specially designed examination tool to solve all your preparation worries. The education system is witnessing unparalleled growth with technology. The new technology module came into existence with a revolutionary change in the pattern and arrangements of exam system reducing time and manual efforts. As the conduction of exam process is also different for all institutions, ensuring the best software embedded with awesome features will support in overall examination process. The primary functionality of iCampus BEAT is to manage student exam data efciently. Maintain security in exams with automated processes. Traditional examination and result computation system involve huge volumes of answer-paper scripts that become difcult to manage and are vulnerable to risks that include damaged and misplaced answer scripts. The result system also requires manual sorting, evaluation and safeguarding, which contributes to the risk of errors and biases, security concerns and may ultimately cause delays in announcing the results.
            </p>

            <p>
              DBS iCampus BEAT facilitates you to create results in a diligent manner from planning and preparing to evaluating and results by keeping in mind the widespread education system.
            </p>

            <p>
              DBS iCampus BEAT result processing system speeds up the whole process, helping deliver results quickly. Thus, streamlines the entire process benetting all stakeholders.
            </p>
          </div>
        </section>

        <section className="beat-modules-section">
          <div className="beat-section-header beat-reveal-scroll">
            <h2 className="beat-section-title">Core <span className="accent-beat">Ecosystem</span></h2>
            <div className="beat-divider"></div>
          </div>

          <div className="beat-grid-layout">
            {modules.map((m, idx) => (
              <div
                key={idx}
                ref={el => cardRefs.current[idx] = el}
                className="beat-interactive-card beat-reveal-scroll"
                onMouseMove={(e) => handleMouseMove(e, idx)}
                onMouseLeave={() => handleMouseLeave(idx)}
              >
                <div className="beat-card-shimmer"></div>
                <div className="beat-card-content">
                  <div className="beat-card-head">
                    <div className="beat-icon-wrap">{m.icon}</div>
                    <span className="beat-status-label">{m.badge}</span>
                  </div>
                  <h3>{m.title}</h3>
                  <div className="beat-image-frame">
                    <img src={m.img} alt={m.title} className="beat-thumb" />
                  </div>
                  <ul className="beat-feat-list">
                    {m.points.map((p, i) => (
                      <li key={i}><CheckCircle size={14} className="beat-check" /> {p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="beat-summary-banner beat-reveal-scroll">
          <div className="summary-glass-beat">
            <div className="summary-left">
              <h3>Why Choose iCampus BEAT?</h3>
              <div className="summary-check-grid">
                <p><Zap size={18} /> Full lifecycle automation</p>
                <p><Zap size={18} /> Secured biometric login</p>
                <p><Zap size={18} /> Dashboard visualizations</p>
                <p><Zap size={18} /> Real-time portal sync</p>
              </div>
            </div>
            <div className="summary-right">
              <button className="btn-action-beat">Request Demo</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ICampusBEAT;
